import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { Navigate, useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { useTrainingCourses } from "../../../../../hooks/useTrainingCourses"
import { digitalManagerApi } from "../../../../../services/api"
import { FieldRow, FormPage } from "../../../../components/FormPage"
import {
  createTrainingCourseValidationSchema,
  useTrainingCourseFieldRenderers,
} from "../../../../components/Helpers"
import { UserContext } from "../../../../context/UserContext"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
  hasUserAccessToApplicationFeature,
} from "../../../../models/Authorization"
import { TrainingGet } from "../../../../models/Training"
import { TrainingCoursePost } from "../../../../models/TrainingCourse"
import { getApiErrorMessage } from "../../../../utils/getErrorMessage"

export function TrainingCourseCreate() {
  const { trainingId } = useParams()
  const { user } = useContext(UserContext)
  const [training, setTraining] = useState<TrainingGet>()
  const navigate = useNavigate()
  const { trainingCourses } = useTrainingCourses(trainingId)
  const returnURL = `/trainings/${trainingId}#courses`

  useEffect(() => {
    if (trainingId) {
      digitalManagerApi
        .get<TrainingGet>(`/trainings/${trainingId}`)
        .then((res) => {
          setTraining(res.data)
        })
        .catch(() => {
          navigate(returnURL)
        })
    }
  }, [navigate, trainingId, returnURL])

  const validationSchema = useMemo(
    () => createTrainingCourseValidationSchema(trainingCourses),
    [trainingCourses]
  )

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty, isValid },
  } = useForm<TrainingCoursePost>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  useEffect(() => {
    reset({
      name: "",
      title: "",
      description: "",
      type: "",
      contentUrl: "",
      duration: "",
      disclaimer: "",
    })
  }, [reset])

  const onSubmit = useCallback(
    (values: TrainingCoursePost) =>
      digitalManagerApi
        .post(`/trainings/${trainingId}/courses`, {
          name: values.name,
          title: values.title,
          description: values.description,
          type: values.type,
          contentUrl: values.contentUrl,
          duration: values.duration,
          disclaimer: values.disclaimer ? values.disclaimer : null,
          position: values.position,
        })
        .then((res) => {
          if (res.status === StatusCodes.CREATED) {
            toast.success("Course created")
            navigate(returnURL)
          }
        })
        .catch((err) => {
          toast.error(getApiErrorMessage(err, "Cannot create course"))
        }),
    [navigate, returnURL, trainingId]
  )

  const { renderTextField, renderNumberField, renderTypeSelect } =
    useTrainingCourseFieldRenderers({ errors })

  if (
    training &&
    !hasUserAccessToApplicationFeature(
      user?.authorizations.applications ?? [],
      training.application.id,
      DimaFeatureShortNameEnum.TM,
      AccessLevel.Write
    )
  ) {
    return <Navigate to={returnURL} />
  }

  return (
    <FormPage
      returnURL={returnURL}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
      isDirty={isDirty}
      isValid={isValid}
    >
      <FieldRow label="Name">
        <Controller name="name" control={control} render={renderTextField} />
      </FieldRow>
      <FieldRow label="Title">
        <Controller name="title" control={control} render={renderTextField} />
      </FieldRow>
      <FieldRow label="Description">
        <Controller name="description" control={control} render={renderTextField} />
      </FieldRow>
      <FieldRow label="Type">
        <Controller name="type" control={control} render={renderTypeSelect} />
      </FieldRow>
      <FieldRow label="Content URL">
        <Controller name="contentUrl" control={control} render={renderTextField} />
      </FieldRow>
      <FieldRow label="Duration">
        <Controller name="duration" control={control} render={renderTextField} />
      </FieldRow>
      <FieldRow label="Disclaimer">
        <Controller name="disclaimer" control={control} render={renderTextField} />
      </FieldRow>
      <FieldRow label="Position">
        <Controller name="position" control={control} render={renderNumberField} />
      </FieldRow>
    </FormPage>
  )
}
